// RolesPermissions.js
import { useState, useEffect } from "react";
import {
  getRolePermissionsMapping,
  updateRolePermissionsMapping,
} from "../../service/Mainservice";
import Loader from "../../Loader/Loader";
import Modal from "../Model/Model";
import "./RolesPermissions.css";

// MUI Icons
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";

const RolesPermissions = () => {
  const [loading, setLoading] = useState(true);
  const [mapping, setMapping] = useState({});
  const [saving, setSaving] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null); // null = add mode
  const [roleName, setRoleName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  // All available permissions (hardcoded from API)
  const allPermissions = [
    "manage_orders",
    "manage_support",
    "manage_stores",
    "approve_stores",
    "manage_drivers",
    "approve_drivers",
    "manage_products",
    "manage_finance",
    "manage_settings",
    "manage_team",
    "support_tickets",
    "manage_marketing",
    "view_reports",
    "refund",
  ];

  const fetchMapping = async () => {
    setLoading(true);
    try {
      const res = await getRolePermissionsMapping();
      // The API returns data as a stringified JSON
      const parsedData =
        typeof res.data === "string" ? JSON.parse(res.data) : res.data;
      setMapping(parsedData || {});
    } catch (err) {
      console.error(err);
      alert("Failed to load role permissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMapping();
  }, []);

  // Open modal for adding new role
  const openAddModal = () => {
    setEditingRole(null);
    setRoleName("");
    setSelectedPermissions([]);
    setModalOpen(true);
  };

  // Open modal for editing existing role
  const openEditModal = (role) => {
    setEditingRole(role);
    setRoleName(role);
    setSelectedPermissions(mapping[role] || []);
    setModalOpen(true);
  };

  const handleSaveRole = () => {
    if (!roleName.trim()) {
      alert("Please enter a role name");
      return;
    }
    const newMapping = { ...mapping };
    // If editing, remove old role name if it changed
    if (editingRole && editingRole !== roleName) {
      delete newMapping[editingRole];
    }
    newMapping[roleName] = selectedPermissions;
    setMapping(newMapping);
    setModalOpen(false);
  };

  const handleDeleteRole = (role) => {
    if (!window.confirm(`Are you sure you want to delete the role "${role}"?`))
      return;
    const newMapping = { ...mapping };
    delete newMapping[role];
    setMapping(newMapping);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await updateRolePermissionsMapping({ rolePermissions: mapping });
      alert("Permissions updated successfully");
    } catch (err) {
      alert(err.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (perm) => {
    setSelectedPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm],
    );
  };

  if (loading) return <Loader />;

  const roleNames = Object.keys(mapping).sort();

  return (
    <div className="roles-permissions-page">
      <div className="page-actions">
        <button className="btn-primary" onClick={openAddModal}>
          <AddIcon /> Add Role
        </button>
        <button
          className="btn-primary"
          onClick={handleSaveAll}
          disabled={saving}
        >
          <SaveIcon /> {saving ? "Saving..." : "Save All"}
        </button>
      </div>

      <div className="table-wrapper">
        <table className="data_table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Permissions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {roleNames.length === 0 ? (
              <tr>
                <td colSpan="3">No roles defined.</td>
              </tr>
            ) : (
              roleNames.map((role) => (
                <tr key={role}>
                  <td>
                    <strong>{role}</strong>
                  </td>
                  <td>
                    <span className="permission-count">
                      {mapping[role].length} permissions
                    </span>
                    <span className="permission-preview">
                      {mapping[role].slice(0, 3).join(", ")}
                      {mapping[role].length > 3 && "..."}
                    </span>
                  </td>
                  <td>
                    {" "}
                    <div className="action_buttons">
                      <EditIcon
                        sx={{
                          color: "var(--red)",
                          fontSize: "28px",
                          cursor: "pointer",
                        }}
                        onClick={() => openEditModal(role)}
                      />
                      <DeleteIcon
                        sx={{
                          color: "var(--red)",
                          fontSize: "28px",
                          cursor: "pointer",
                        }}
                        onClick={() => handleDeleteRole(role)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingRole ? `Edit Role: ${editingRole}` : "Add New Role"}
        maxWidth="600px"
      >
        <div className="role-modal">
          <div className="form-group">
            <label>Role Name</label>
            <input
              type="text"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              placeholder="e.g. Sales Manager"
            />
          </div>

          <div className="form-group">
            <label>Permissions</label>
            <div className="permissions-grid">
              {allPermissions.map((perm) => (
                <label key={perm} className="permission-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedPermissions.includes(perm)}
                    onChange={() => togglePermission(perm)}
                  />
                  {perm.replace(/_/g, " ").toUpperCase()}
                </label>
              ))}
            </div>
          </div>

          <div className="modal-actions">
            <button
              className="btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button className="btn-primary" onClick={handleSaveRole}>
              Save Role
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RolesPermissions;
